import { useAuthStore } from '@/stores/authStore';
import { useTheme } from './ThemeProvider';
import { isFixedTierRole } from './roleTier';
import type { DashboardComponentMap } from './types';

/**
 * Themed-tier counterpart to `ThemeSlot` (ADR Section 16, ADR-2026-07-D). Renders the active
 * theme's `DashboardComponentMap` entry for Guest/Org Admin/Host/Property Manager/Staff routes,
 * wrapped in the same `.theme-<key>` scoping class `ThemeSlot` uses.
 *
 * Fixed-tier roles (Super Admin/Platform Admin/Platform Staff) never route through this
 * component — their `/dashboard/super-admin/*`, `/dashboard/platform-admin/*`, and
 * `/dashboard/platform-staff/*` trees keep the pre-existing `DashboardLayout`/plain page imports
 * untouched. The fixed-tier check here is a defensive guard only (e.g. a stale render during a
 * role-based redirect), not the primary gating mechanism — RBAC route guards own that.
 */
/**
 * Only the zero-prop page/shell keys route through here. Modal keys (ImageLightbox, etc.) need
 * runtime props (open state, callbacks, target data) supplied by the page that opens them — each
 * theme's own page components import and use those directly rather than resolving them as a
 * route-level slot.
 */
type DashboardPageKey = Extract<
  keyof DashboardComponentMap,
  | 'DashboardShell'
  | 'GuestOverviewPage'
  | 'PropertyOverviewPage'
  | 'StaffOverviewPage'
  | 'MyBookingsPage'
  | 'MyTripsPage'
  | 'TripDetailPage'
  | 'FavoritesPage'
  | 'GuestMessagesPage'
  | 'GuestReviewsPage'
  | 'PropertiesManagementPage'
  | 'PropertyCreateEditPage'
  | 'ExperienceCreatePage'
  | 'TeamManagementPage'
  | 'BookingsManagementPage'
  | 'GuestsDirectoryPage'
  | 'AnalyticsPage'
  | 'CommissionPage'
  | 'PayoutSettingsPage'
  | 'CalendarSyncPage'
  | 'ChannelSyncPage'
  | 'ReviewsManagementPage'
  | 'PoliciesPage'
  | 'AuditLogPage'
  | 'CompliancePage'
  | 'HostMessagesPage'
  | 'StaffTasksPage'
  | 'StaffSchedulePage'
  | 'ProfileSettingsPage'
>;

export function DashboardThemeSlot({ page }: { page: DashboardPageKey }) {
  const { user } = useAuthStore();
  const role = user?.roles?.[0];
  const { themeKey, components } = useTheme();

  if (isFixedTierRole(role)) {
    return null;
  }

  const Component = components.dashboard[page];
  return (
    <div className={`theme-${themeKey}`}>
      <Component />
    </div>
  );
}
